import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { isName, isPhone, isRRNBack, isRRNFront } from "../../../utils/validate";
import { CheckIcon } from "@heroicons/react/24/solid"; // npm i @heroicons/react
import { formatPhone } from "../../../utils/fomatPhone";


export default function SignUpInfoFields({ onNext, onValidChange }) {
    const [name, setName] = useState("");          // 이름
    const [phone, setPhone] = useState("");     // 전화번호
    const [front, setFront] = useState("");   // 주민번호 앞 6자리
    const [back, setBack] = useState("");   // 주민번호 뒤 7자리
    const [agree, setAgree] = useState({
        all: false,
        personal: false,
        tos: false,
        marketing: false,
    });

    const backRef = useRef(null);
    const nameValid = isName(name);
    const phoneValid = isPhone(phone);
    const frontValid = isRRNFront(front);
    const backValid = /^[0-9]$/.test(back);
    const rrnValid = frontValid && backValid;
    const rrnTouched = front.length > 0 || back.length > 0;
    const requiredAgree = agree.personal && agree.tos;
    const formValid = nameValid && phoneValid && rrnValid && requiredAgree;

    const toggleAgree = (key) => {
        if (key === "all") {
            const nextVal = !agree.all;
            setAgree({
                all: nextVal,
                personal: nextVal,
                tos: nextVal,
                marketing: nextVal,
            });
        } else {
            const next = { ...agree, [key]: !agree[key] };
            next.all = next.personal && next.tos && next.marketing;
            setAgree(next);
        }
    };

    /* 앞자리 6자리 입력 완료 → 다음 칸 포커스 */
    const handleFrontChange = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
        setFront(v);
        if (v.length === 6) backRef.current?.focus();
    };

    const handleBackChange = (e) => {
        setBack(e.target.value.replace(/\D/g, "").slice(0, 1));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formValid) return;

        const phoneDigits = phone.replace(/\D/g, "");

        onNext({
            name,
            rrn_front: front,
            rrn_back1: back,          // 뒤 1자리
            phone: phoneDigits,
            consent_personal: agree.personal,
            consent_tos: agree.tos,
            consent_marketing: agree.marketing,
        });
    };

    const Checkbox = ({ id }) => (
        <span
            onClick={() => toggleAgree(id)}
            className={clsx(
                "w-6 h-6 flex items-center justify-center rounded-full cursor-pointer transition-colors",
                agree[id] ? "bg-[#5F9BFF]" : "bg-white border border-gray-300"
            )}
        >
            {agree[id] && <CheckIcon className="w-4 h-4 text-white" />}
        </span>
    );

    useEffect(() => {
        onValidChange?.(formValid);        // prop 이 있으면 호출
    }, [formValid, onValidChange]);


    return (
        <form onSubmit={handleSubmit} className="flex flex-col h-full flex-1">
            <div className="py-8">
                <p className="text-2xl md:text-3xl leading-tight">회원 정보 입력</p>
                <p className="text-sm md:text-lg text-[#4A8AFF] mt-2">
                    본인 확인을 위해 회원님의 고유 정보가 필요합니다.
                </p>
            </div>

            <div className="flex-1  flex flex-col gap-6">
                {/* 이름 */}
                <div>
                    <label className="block text-sm md:text-lg font-medium mb-1">
                        이름
                        {!nameValid && name && (
                            <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                                필수 항목이 제대로 입력되지 않았습니다.
                            </span>
                        )}
                    </label>

                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="홍길동"
                        className={clsx(
                            "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                            !nameValid && name && "border-[#DE4F4F]"
                        )}
                    />
                </div>

                {/* 주민번호 */}
                <div className="flex flex-col">
                    <label className="block text-sm md:text-lg font-medium mb-1">
                        주민등록번호
                        {rrnTouched && !rrnValid && (
                            <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                                필수 항목이 제대로 입력되지 않았습니다.
                            </span>
                        )}
                    </label>

                    <div className="flex items-center gap-2">
                        {/* 앞 6자리 */}
                        <input
                            type="text"
                            value={front}
                            onChange={handleFrontChange}
                            placeholder="앞 6자리 입력"
                            maxLength={6}
                            className={clsx(
                                "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                                front && !frontValid && "border-[#DE4F4F]"
                            )}
                        />

                        <span className="text-sm text-[#DBDBDB]">–</span>

                        {/* 뒤 7자리 */}
                        <div className="relative w-full">
                            {/* 실제로 값이 들어가는 input ― 글자는 투명 처리 */}
                            <input
                                type="text"
                                value={back}
                                onChange={handleBackChange}
                                maxLength={1}
                                ref={backRef}
                                className={clsx(
                                    "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                                    "text-transparent caret-black",
                                    back && !backValid && "border-[#DE4F4F]"
                                )}
                            />

                            {/* 사용자가 보게 될 가짜 값(앞 1자리 + ● 6개) */}
                            {back && (
                                <span className="absolute inset-0 flex items-center px-3 pointer-events-none select-none">
                                    {back + "●".repeat(6)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div>
                    <label className="block text-sm md:text-lg font-medium mb-1">
                        전화번호
                        {!phoneValid && phone && (
                            <span className="ml-4 text-[#DE4F4F] text-xs md:text-sm">
                                필수 항목이 제대로 입력되지 않았습니다.
                            </span>
                        )}
                    </label>

                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        placeholder="010-1234-5678"
                        className={clsx(
                            "w-full border rounded-lg px-3 py-3.5 bg-[#F1F1F1] outline-none text-sm md:text-lg",
                            !phoneValid && phone && "border-[#DE4F4F]"
                        )}
                    />
                </div>

                <div className="flex flex-col gap-3 pt-1">
                    <p className="font-medium text-sm md:text-lg">서비스 약관 동의</p>

                    {/* 전체 동의 */}
                    <label className="flex flex-nowrap items-center gap-3 w-full cursor-pointer select-none">
                        <Checkbox id="all" />
                        <span className="text-[#5F9BFF] font-semibold text-sm md:text-lg">
                            약관 전체 동의
                        </span>
                    </label>

                    <hr className="border-gray-200 my-1" />

                    {/* 개인정보 */}
                    <label className="flex flex-nowrap items-center gap-3 w-full cursor-pointer select-none">
                        <span className="flex-shrink-0">
                            <Checkbox id="personal" />
                        </span>
                        <span className="flex-1 break-words text-sm md:text-lg">
                            [필수] 개인정보 제공 동의 – 회원번호, 이메일 주소, 이름, 성별, 연락처, 생년월일, 생일
                        </span>
                    </label>


                    {/* 서비스 약관 */}
                    <label className="flex flex-nowrap items-center gap-3 w-full cursor-pointer select-none">
                        <span className="flex-shrink-0">
                            <Checkbox id="tos" />
                        </span>
                        <span className="text-sm md:text-lg">
                            [필수] 서비스 약관 이용 동의
                        </span>
                    </label>

                    {/* 마케팅 */}
                    <label className="flex flex-nowrap items-center gap-3 w-full cursor-pointer select-none">
                        <span className="flex-shrink-0">
                            <Checkbox id="marketing" />
                        </span>
                        <span className="text-sm md:text-lg">
                            [선택] 홍보성 이벤트 SMS 수신 동의
                        </span>
                    </label>
                </div>
            </div>
            <button
                type="submit"
                disabled={!formValid}
                className="mt-6 mb-6 w-full py-3 rounded-xl bg-[#5F9BFF] text-white
                   disabled:bg-[#F1F1F1] disabled:text-gray-400">
                다음으로
            </button>
        </form>
    );
}
